import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API,
  // Cookie-Auth (Juni 2026): httpOnly access_token-Cookie wird mitgesendet.
  withCredentials: true,
});

// Feb 2026 — Pydantic 422 Validation-Errors menschenlesbar machen.
// Backend liefert detail: [{ loc: [...], msg, type }, ...]. Wir normalisieren zu einem String,
// damit alle bestehenden `toast.error(error.response?.data?.detail || '...')`-Patterns weiterhin funktionieren.
const FIELD_LABELS = {
  postal_code: 'PLZ',
  vat_id: 'USt-IdNr.',
  phone: 'Telefon',
  iban: 'IBAN',
  tax_office_iban: 'Finanzamt-IBAN',
  che_number: 'UID/CHE-Nummer',
  tax_id: 'Steuernummer',
  tax_number: 'Steuernummer',
  email: 'E-Mail',
  name: 'Name',
  street: 'Straße',
  street_number: 'Hausnummer',
  city: 'Stadt',
  country_code: 'Land',
  inline_customer: 'Neuer Kunde',
  customer_id: 'Kunde',
  project_id: 'Projekt',
  invoice_date: 'Rechnungsdatum',
  due_date: 'Fälligkeitsdatum',
  service_date: 'Leistungsdatum',
  unit_price: 'Einzelpreis',
  quantity: 'Menge',
  tax_rate: 'USt-Satz',
  amount: 'Betrag',
  net_amount: 'Nettobetrag',
};

function humanizeFieldPath(loc) {
  if (!Array.isArray(loc)) return '';
  // loc = ['body', 'inline_customer', 'postal_code'] → wir wollen nur die sprechenden Teile
  const skip = new Set(['body', 'query', 'path', 'header', 'cookie']);
  const parts = loc.filter((p) => !skip.has(p)).map((p) => FIELD_LABELS[p] || p);
  return parts.join(' → ');
}

function cleanMsg(msg) {
  if (!msg) return '';
  // Pydantic field_validators prefixen mit "Value error, "
  return String(msg).replace(/^Value error,\s*/i, '').trim();
}

function formatPydanticDetail(detailArr) {
  if (!Array.isArray(detailArr) || detailArr.length === 0) return 'Ungültige Eingabe.';
  return detailArr
    .map((d) => {
      const field = humanizeFieldPath(d.loc);
      const msg = cleanMsg(d.msg);
      return field ? `${field}: ${msg}` : msg;
    })
    .filter(Boolean)
    .join('\n');
}

// Request interceptor: Cookie-Auth (T1) — kein Token aus localStorage mehr.
// Das httpOnly access_token-Cookie wird via withCredentials automatisch mitgesendet.
api.interceptors.request.use(
  (config) => config,
  (error) => Promise.reject(error)
);

// Response interceptor: (a) normalisiert Pydantic-422-Arrays zu String, (b) 401-Auto-Logout.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // (a) Pydantic 422: detail als Array → menschenlesbarer String
    const detail = error.response?.data?.detail;
    if (Array.isArray(detail)) {
      error.response.data.detail = formatPydanticDetail(detail);
    }

    // (a2) Gast Read-only 403: detail ist ein Objekt {code, message}. Wir feuern das globale
    // "Konto sichern"-Event (GuestBanner hört darauf) UND normalisieren detail zu einem String,
    // damit lokale toast.error/Modal-Handler kein Objekt rendern (sonst React-White-Screen).
    if (error.response?.status === 403 && detail && typeof detail === 'object' && detail.code === 'guest_readonly') {
      error.isGuestReadonly = true;
      try { window.dispatchEvent(new CustomEvent('ivoliq:guest-readonly', { detail })); } catch { /* ignore */ }
      error.response.data.detail = detail.message || 'Nur im Live-Modus verfügbar. Sichere dir dein kostenloses Konto.';
    }

    // (b) 401-Handling (wie gehabt)
    if (error.response?.status === 401) {
      const errorDetailRaw = error.response?.data?.detail;
      const errorDetail = typeof errorDetailRaw === 'string' ? errorDetailRaw : '';
      const requestUrl = error.config?.url || '';
      
      // Don't auto-logout for business logic 401 errors (wrong password, etc.)
      // Only auto-logout for authentication-related 401 errors
      const isAuthenticationError = 
        errorDetail.includes('Not authenticated') ||
        errorDetail.includes('Invalid token') ||
        errorDetail.includes('Token expired') ||
        errorDetail.includes('Token has expired') ||
        errorDetail.includes('Could not validate credentials') ||
        errorDetail.includes('Authentication required') ||
        // If no specific error detail, assume it's an auth error (for backward compatibility)
        (!errorDetail && !requestUrl.includes('/auth/delete-account') && !requestUrl.includes('/auth/change-password'));
      
      if (isAuthenticationError) {
        // Clear token and redirect to login for real authentication errors
        localStorage.removeItem('auth_token');
        window.location.href = '/login';
      }
      // For business logic errors (like wrong password), let the component handle it
    }
    return Promise.reject(error);
  }
);

export default api;
